// src/pages/QuestionnairePage/steps/Step2SkinType.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Droplets } from 'lucide-react';

const StepCard = styled(motion.div)`
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-medium);
  border-radius: 24px;
  padding: 32px 24px;
  box-shadow:
    0 8px 32px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;
  container-type: inline-size; /* ✅ Включаем контейнерные запросы */

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  /* ✅ Адаптивные отступы с учётом safe-area */
  @media (max-width: 375px) {
    padding: 28px calc(20px + env(safe-area-inset-right, 0px)) 28px calc(20px + env(safe-area-inset-left, 0px));
  }

  [data-theme="dark"] & {
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::before {
      background: linear-gradient(90deg, #9d8eee, #a0d8f0);
    }
  }
`;

const Question = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 12px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #7B68EE;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    svg {
      color: #C4B5FD;
    }
  }
`;

const Hint = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  line-height: 1.5;
  padding: 12px;
  background: var(--surface-medium);
  border-radius: 8px;
  border-left: 3px solid #7B68EE;

  [data-theme="dark"] & {
    border-left-color: #C4B5FD;
  }
`;

/**
 * ✅ АДАПТИВНАЯ СЕТКА с контейнерными запросами
 *
 * Fixes:
 * - Убрано смещение/горизонтальный скролл на iPhone 14 Pro Max
 * - Адаптивная сетка: всегда 2x2 для читаемости длинных текстов
 * - Safe-area учтён через padding в родительском контейнере
 * - Контейнерные запросы для точной адаптации
 */
const ChipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Всегда 2 колонки для удобства */
  gap: 12px;
  width: 100%;
  max-width: 100%; /* Предотвращаем выход за границы */
  margin: 0 auto;
  padding: 0 env(safe-area-inset-right, 0px) 0 env(safe-area-inset-left, 0px);

  /* ✅ Адаптация под планшеты и десктоп - макс ширина 600px */
  @media (min-width: 600px) {
    max-width: 600px;
    gap: 16px; /* Больше отступ на широких экранах */
  }
`;

const ChipButton = styled(motion.button).attrs({ type: 'button' })<{ $active: boolean }>`
  padding: 18px 20px !important;
  border-radius: 16px !important;
  border: 2px solid ${props => props.$active
    ? '#7B68EE'
    : 'rgba(123, 104, 238, 0.2)'
  } !important;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #7B68EE, #9966CC)'
    : 'rgba(255, 255, 255, 0.92)'
  } !important;
  color: ${props => props.$active
    ? '#FFFFFF'
    : '#000000'
  } !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  box-shadow: ${props => props.$active
    ? '0 8px 22px rgba(123, 104, 238, 0.35)'
    : '0 4px 12px rgba(15, 23, 42, 0.08)'
  } !important;
  display: flex !important;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  outline: none !important;

  &:hover {
    transform: translateY(-2px);
    border-color: #7B68EE !important;
    box-shadow: 0 10px 28px rgba(123, 104, 238, 0.35) !important;
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    border-color: ${props => props.$active
      ? '#C4B5FD'
      : 'rgba(157, 142, 238, 0.3)'
    } !important;
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #9d8eee, #b896dc)'
      : 'rgba(255, 255, 255, 0.08)'
    } !important;
    color: ${props => props.$active
      ? '#FFFFFF'
      : '#FFFFFF'
    } !important;
    box-shadow: ${props => props.$active
      ? '0 8px 22px rgba(157, 142, 238, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.3)'
    } !important;

    &:hover {
      border-color: #C4B5FD !important;
      box-shadow: 0 10px 28px rgba(157, 142, 238, 0.45) !important;
    }
  }
`;

const ChipEmoji = styled.span`
  font-size: 24px;
`;

const ChipLabel = styled.span`
  font-size: 15px;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
`;

interface Step2SkinTypeProps {
  value: 'normal' | 'oily' | 'dry' | 'combination' | null;
  onChange: (type: 'normal' | 'oily' | 'dry' | 'combination') => void;
}

export const Step2SkinType: React.FC<Step2SkinTypeProps> = ({ value, onChange }) => {
  const types =[
    { value: 'dry' as const, label: 'Сухая', emoji: '🌵' },
    { value: 'normal' as const, label: 'Нормальная', emoji: '✨' },
    { value: 'combination' as const, label: 'Комбинированная', emoji: '🌗' },
    { value: 'oily' as const, label: 'Жирная', emoji: '💧' },
  ];

  return (
    <StepCard
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Question>
        <Droplets size={24} />
        Какой у Вас тип кожи?
      </Question>

      <Hint>
        💡 <strong>Подсказка:</strong> Обратите внимание на размер пор кожи лица.
        Если поры практически незаметны — вероятно у Вас сухой или нормальный тип кожи.
        Если поры расширенные в Т-зоне (лоб, нос, подбородок) — то комбинированный.
        Если расширены по всему лицу, включая область щек — Ваш тип кожи жирный.
      </Hint>
      
      <ChipGrid>
        {types.map((type, index) => (
          <ChipButton
            key={type.value}
            $active={value === type.value}
            onClick={() => onChange(type.value)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
          >
            <ChipEmoji>{type.emoji}</ChipEmoji>
            <ChipLabel>{type.label}</ChipLabel>
          </ChipButton>
        ))}
      </ChipGrid>
    </StepCard>
  );
};